import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminAuth } from '../services/adminAuth';
import { memberAuth } from '../services/memberAuth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);



  const checkCookieAuth = async () => {
    try {
      const response = await fetch('https://advanced-manifestation-magic.samrruddhi.in/api/member/check-auth', {
        method: 'GET',
        credentials: 'include', // Include cookies
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.member));
          localStorage.setItem('userType', 'member');
          setUser(data.member);
          setUserType('member');
          return true;
        }
      }
    } catch (error) {
      console.error('Cookie auth check failed:', error);
    }
    return false;
  };

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      const storedUserType = localStorage.getItem('userType');

      if (token && storedUser && storedUserType) {
        setUser(JSON.parse(storedUser));
        setUserType(storedUserType);


        const freshUserData = await memberAuth.me();
        console.log('Fresh user data:', freshUserData);
        setUser(freshUserData);

        setLoading(false);
        return;
      }

      // Only check cookie auth if we're on a member route and no localStorage data
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/member/') && currentPath !== '/member/login') {
        const cookieAuthSuccess = await checkCookieAuth();
        if (cookieAuthSuccess) {
          setLoading(false);
          return;
        }
      }

      // Clear any invalid data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userType');
      setUser(null);
      setUserType(null);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userType');
      setUser(null);
      setUserType(null);
    } finally {
      setLoading(false);
    }
  };

  // Simplify login method for members
  const login = async (credentials, type = 'admin') => {
    try {
      let result;
      if (type === 'admin') {
        result = await adminAuth.login(credentials);
        setUser(result.admin);
        setUserType(type);
        return result;
      } else if (type === 'member') {
        // For members, don't use this method - handle directly in login component
        throw new Error('Use direct fetch for member login');
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (userType === 'admin') {
        await adminAuth.logout();
      } else if (userType === 'member') {
        // Use fetch with credentials for member logout
        await fetch('https://advanced-manifestation-magic.samrruddhi.in/api/member/logout', {
          method: 'POST',
          credentials: 'include', // Include cookies
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Accept': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userType');
      setUser(null);
      setUserType(null);
    }
  };

  const isAuthenticated = () => {
    return !!user && !!userType;
  };

  const value = {
    user,
    userType,
    loading,
    login,
    logout,
    isAuthenticated,
    checkAuth,
    setUser,        // Add this
    setUserType     // Add this
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};